import {
  OPTION_ALERT_TYPES,
  OPTION_DIRECTIONS,
  OPTION_EVIDENCE_STATUSES,
  OPTION_FACTOR_KEYS,
  OPTION_FACTOR_STATUSES,
  OPTION_POSITION_STRUCTURES,
  OPTION_PURCHASE_TYPES,
  OPTION_RISK_LEVELS,
  OPTION_SNAPSHOT_KEYS,
  OPTION_STATES,
  OPTION_STRATEGIES,
  OPTION_THRESHOLDS,
  OPTION_TYPES,
  OPTIONS_SCHEMA_VERSION,
  OPTIONS_SUBMODES,
} from "./NinaOptionsScannerSchema";

export const roundOptionNumber = (value, places = 4) => {
  const factor = 10 ** places;
  const rounded = Math.round((value + Number.EPSILON) * factor) / factor;
  return Object.is(rounded, -0) ? 0 : rounded;
};

export const deriveOptionFinancials = (snapshot) => {
  const spread = roundOptionNumber(snapshot.ask - snapshot.bid);
  const premium = roundOptionNumber(snapshot.ask * snapshot.contractMultiplier);
  const maxLoss = premium;
  const activityRatio =
    snapshot.openInterest === 0
      ? null
      : roundOptionNumber(snapshot.volume / snapshot.openInterest);
  const breakeven = roundOptionNumber(
    snapshot.contractType === "call"
      ? snapshot.strike + snapshot.ask
      : snapshot.strike - snapshot.ask
  );
  const distanceToBreakevenPercent = roundOptionNumber(
    (Math.abs(breakeven - snapshot.underlyingPrice) /
      snapshot.underlyingPrice) *
      100
  );
  return {
    spread,
    premium,
    maxLoss,
    volumeOpenInterestRatio: activityRatio,
    breakeven,
    distanceToBreakevenPercent,
  };
};

const fail = (message) => ({ valid: false, error: message, value: null });
const finite = (value) => typeof value === "number" && Number.isFinite(value);
const canonical = (value, places = 4) =>
  finite(value) && Math.abs(value - roundOptionNumber(value, places)) <= 1e-9;
const timestamp = (value) =>
  typeof value === "string" && Number.isFinite(Date.parse(value));
const exactKeys = (value, allowed, label) => {
  const keys = Object.keys(value);
  const unexpected = keys.find((key) => !allowed.includes(key));
  if (unexpected) return `${label} contains unexpected key: ${unexpected}`;
  const missing = allowed.find((key) => !keys.includes(key));
  return missing ? `${label} is missing required key: ${missing}` : "";
};

const validateFactor = (factor, key) => {
  if (!factor || typeof factor !== "object" || Array.isArray(factor)) {
    return `${key} must be an object`;
  }
  const shape = exactKeys(
    factor,
    ["status", "score", "source", "evidenceStatus", "explanation"],
    key
  );
  if (shape) return shape;
  if (!OPTION_FACTOR_STATUSES.includes(factor.status)) {
    return `${key} has an invalid status`;
  }
  if (
    typeof factor.source !== "string" ||
    !factor.source.trim() ||
    typeof factor.explanation !== "string" ||
    !factor.explanation.trim()
  ) {
    return `${key} requires provenance and explanation`;
  }
  if (!OPTION_EVIDENCE_STATUSES.includes(factor.evidenceStatus)) {
    return `${key} has an invalid evidence status`;
  }
  if (factor.status === "unavailable") {
    if (factor.score !== null || factor.evidenceStatus !== "unavailable") {
      return `${key} unavailable evidence cannot contain a usable score`;
    }
    return "";
  }
  if (!finite(factor.score) || factor.score < 0 || factor.score > 100) {
    return `${key} score must be a finite number from 0 to 100`;
  }
  if (factor.evidenceStatus === "unavailable") {
    return `${key} available evidence cannot be labeled unavailable`;
  }
  if (factor.status === "positive" && factor.score < 50) {
    return `${key} positive status conflicts with its score`;
  }
  if (factor.status === "negative" && factor.score > 50) {
    return `${key} negative status conflicts with its score`;
  }
  if (
    factor.status === "conflicting" &&
    (factor.score < 35 || factor.score > 65)
  ) {
    return `${key} conflicting status must remain near the neutral range`;
  }
  return "";
};

export function validateOptionSnapshot(snapshot, { now = Date.now() } = {}) {
  if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) {
    return fail("Option snapshot must be an object.");
  }
  const shape = exactKeys(snapshot, OPTION_SNAPSHOT_KEYS, "Option snapshot");
  if (shape) return fail(shape);
  if (snapshot.schemaVersion !== OPTIONS_SCHEMA_VERSION) {
    return fail(
      snapshot.schemaVersion > OPTIONS_SCHEMA_VERSION
        ? "Unsupported future Options Scanner schema version."
        : "Invalid Options Scanner schema version."
    );
  }
  for (const key of ["id", "contractId", "setup", "source", "demoLabel", "catalyst", "catalystTiming"]) {
    if (typeof snapshot[key] !== "string" || !snapshot[key].trim()) {
      return fail(`${key} must be a non-empty string`);
    }
  }
  if (
    typeof snapshot.symbol !== "string" ||
    !/^[A-Z][A-Z0-9.-]{0,9}$/.test(snapshot.symbol)
  ) {
    return fail("symbol is invalid");
  }
  if (!/^SIM-[A-Z0-9-]+$/.test(snapshot.contractId)) {
    return fail("contractId must be a simulated contract identifier");
  }
  const enumChecks = [
    ["contractType", OPTION_TYPES],
    ["positionStructure", OPTION_POSITION_STRUCTURES],
    ["purchaseType", OPTION_PURCHASE_TYPES],
    ["alertType", OPTION_ALERT_TYPES],
    ["direction", OPTION_DIRECTIONS],
    ["submode", OPTIONS_SUBMODES],
    ["evidenceStatus", OPTION_EVIDENCE_STATUSES],
    ["riskLevel", OPTION_RISK_LEVELS],
    ["underlyingRisk", OPTION_RISK_LEVELS],
    ["state", OPTION_STATES],
  ];
  for (const [key, values] of enumChecks) {
    if (!values.includes(snapshot[key])) return fail(`${key} is invalid`);
  }
  if (!OPTION_STRATEGIES.includes(snapshot.setup)) {
    return fail("setup is not supported for a purchased long single-leg contract");
  }
  if (
    (snapshot.contractType === "call" && snapshot.direction !== "bullish") ||
    (snapshot.contractType === "put" && snapshot.direction !== "bearish")
  ) {
    return fail("direction is inconsistent with the supported long contract type");
  }
  if (snapshot.demoLabel !== "SIMULATED") return fail("demoLabel must be SIMULATED");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(snapshot.expiration)) {
    return fail("expiration must use YYYY-MM-DD");
  }
  const expiration = Date.parse(`${snapshot.expiration}T00:00:00.000Z`);
  if (
    !Number.isFinite(expiration) ||
    new Date(expiration).toISOString().slice(0, 10) !== snapshot.expiration
  ) {
    return fail("expiration is invalid");
  }
  for (const key of ["asOf", "updatedAt"]) {
    if (!timestamp(snapshot[key])) return fail(`${key} must be a valid timestamp`);
    if (Date.parse(snapshot[key]) > now + 5 * 60 * 1000) {
      return fail(`${key} exceeds the five-minute clock-skew tolerance`);
    }
  }
  const asOfDay = Date.parse(snapshot.asOf.slice(0, 10) + "T00:00:00.000Z");
  const expectedDte = Math.round((expiration - asOfDay) / 86400000);
  if (!Number.isInteger(snapshot.dte) || snapshot.dte < 0 || snapshot.dte !== expectedDte) {
    return fail("dte is inconsistent with expiration and asOf");
  }
  const modeThreshold = OPTION_THRESHOLDS[snapshot.submode];
  if (
    snapshot.dte < modeThreshold.dteMin ||
    snapshot.dte > modeThreshold.dteMax
  ) {
    return fail(`dte is invalid for the ${snapshot.submode} classification`);
  }
  const positive = [
    "underlyingPrice",
    "strike",
    "ask",
    "contractMultiplier",
    "premium",
    "maxLoss",
    "breakeven",
  ];
  for (const key of positive) {
    if (!finite(snapshot[key]) || snapshot[key] <= 0) {
      return fail(`${key} must be a finite positive number`);
    }
  }
  for (const key of [
    "underlyingPrice",
    "strike",
    "bid",
    "ask",
    "spread",
    "impliedVolatility",
    "delta",
  ]) {
    if (!canonical(snapshot[key])) {
      return fail(`${key} must use no more than four decimal places`);
    }
  }
  if (!finite(snapshot.bid) || snapshot.bid < 0 || snapshot.bid > snapshot.ask) {
    return fail("bid must be finite, nonnegative, and no greater than ask");
  }
  for (const key of ["spread", "volume", "openInterest", "impliedVolatility", "distanceToBreakevenPercent"]) {
    if (!finite(snapshot[key]) || snapshot[key] < 0) {
      return fail(`${key} must be a finite nonnegative number`);
    }
  }
  if (snapshot.impliedVolatility > 1000) {
    return fail("impliedVolatility cannot exceed the supported 1000% range");
  }
  if (!Number.isInteger(snapshot.volume) || !Number.isInteger(snapshot.openInterest)) {
    return fail("volume and openInterest must be integers");
  }
  if (!Number.isInteger(snapshot.contractMultiplier) || snapshot.contractMultiplier <= 0) {
    return fail("contractMultiplier must be a positive integer");
  }
  if (
    snapshot.volumeOpenInterestRatio !== null &&
    (!finite(snapshot.volumeOpenInterestRatio) || snapshot.volumeOpenInterestRatio < 0)
  ) {
    return fail("volumeOpenInterestRatio must be null or a finite nonnegative number");
  }
  if (!finite(snapshot.delta) || snapshot.delta < -1 || snapshot.delta > 1) {
    return fail("delta must be a finite number from -1 to 1");
  }
  if (
    (snapshot.contractType === "call" && snapshot.delta < 0) ||
    (snapshot.contractType === "put" && snapshot.delta > 0)
  ) {
    return fail("delta sign is inconsistent with contractType");
  }
  if (!finite(snapshot.stability) || snapshot.stability < 0 || snapshot.stability > 100) {
    return fail("stability must be a finite number from 0 to 100");
  }
  if (!snapshot.signals || typeof snapshot.signals !== "object" || Array.isArray(snapshot.signals)) {
    return fail("signals must be an object");
  }
  const signalShape = exactKeys(snapshot.signals, OPTION_FACTOR_KEYS, "signals");
  if (signalShape) return fail(signalShape);
  for (const key of OPTION_FACTOR_KEYS) {
    const factorError = validateFactor(snapshot.signals[key], key);
    if (factorError) return fail(factorError);
  }
  const derived = deriveOptionFinancials(snapshot);
  for (const key of Object.keys(derived)) {
    if (snapshot[key] !== derived[key]) {
      return fail(`${key} is inconsistent with validated base values`);
    }
  }
  return {
    valid: true,
    error: "",
    value: JSON.parse(JSON.stringify(snapshot)),
  };
}
